import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, ChevronDown, LogOut } from 'lucide-react';
import SidebarBranding from './SidebarBranding';
import './CollapsibleSidebar.css';

const CollapsibleSidebar = ({
  logoSrc,
  brandName,
  subtitle = '',
  viewBadge = null,
  whiteLabelCompanyName = null,
  platformName = null,
  menuItems,
  activePath = '',
  onNavigate,
  onLogoClick = null,
  onToggle = null,
  className = '',
  style = null,
  primaryColor = null,
  accentColor = '#DC2626',
  darkMode = true,
  autoCollapse = false,
  onLogout = null
}) => {
  const [collapsed, setCollapsed] = useState(autoCollapse);
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  // Track which section groups are expanded (by section label)
  const [expandedSections, setExpandedSections] = useState({});
  const hoverTimerRef = useRef(null);

  // Detect touch device — disable hover-based autoCollapse on phones/tablets
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    const checkTouch = () => {
      const hasTouch = 'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches;
      const isSmallScreen = window.innerWidth < 1024;
      setIsTouchDevice(hasTouch && isSmallScreen);
    };
    checkTouch();
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  // Effective autoCollapse: disabled on touch/mobile devices
  const effectiveAutoCollapse = autoCollapse && !isTouchDevice;

  // In autoCollapse mode, sidebar expands on hover; otherwise use manual toggle
  const effectiveCollapsed = effectiveAutoCollapse ? !hoverExpanded : collapsed;

  // Handle mouse enter/leave for auto-collapse mode (desktop only)
  const handleSidebarMouseEnter = useCallback(() => {
    if (!effectiveAutoCollapse) return;
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHoverExpanded(true);
  }, [effectiveAutoCollapse]);

  const handleSidebarMouseLeave = useCallback(() => {
    if (!effectiveAutoCollapse) return;
    // Small delay before collapsing to prevent flickering
    hoverTimerRef.current = setTimeout(() => {
      setHoverExpanded(false);
    }, 200);
  }, [effectiveAutoCollapse]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // Notify parent about effective width changes
  useEffect(() => {
    if (onToggle) onToggle(effectiveCollapsed);
  }, [effectiveCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand the section that contains the active path
  useEffect(() => {
    if (!menuItems || !activePath) return;
    const initial = {};
    menuItems.forEach((item) => {
      if (item.type === 'section' && item.children) {
        const hasActive = item.children.some((child) => activePath?.startsWith(child.path));
        if (hasActive) initial[item.label] = true;
      }
    });
    setExpandedSections((prev) => ({ ...prev, ...initial }));
  }, [activePath]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHoveredIndex(null);
  }, [effectiveCollapsed]);

  const handleItemClick = (path, external = false) => {
    if (path) {
      if (external || path.startsWith('http://') || path.startsWith('https://')) {
        window.open(path, '_blank', 'noopener,noreferrer');
      } else if (onNavigate) {
        onNavigate(path);
      }
    }
  };

  const handleToggle = () => {
    if (autoCollapse) return; // In autoCollapse mode, toggle button is hidden
    setCollapsed((prev) => !prev);
    if (onToggle) onToggle(!collapsed);
  };

  const toggleSection = (sectionLabel) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionLabel]: !prev[sectionLabel]
    }));
  };

  const composeClasses = (...parts) => {
    return parts
      .filter(Boolean)
      .map((part) => {
        if (typeof part === 'string') return part;
        if (typeof part === 'object') {
          return Object.entries(part)
            .filter(([, value]) => value)
            .map(([key]) => key)
            .join(' ');
        }
        return '';
      })
      .filter(Boolean)
      .join(' ');
  };

  const hasWhiteLabel = !!primaryColor;
  const textColor = darkMode ? '#f8fafc' : '#111827';
  const subtitleColor = darkMode ? '#9ca3af' : '#6b7280';
  const brandHighlightColor = '#DC2626';
  const brandBaseColor = darkMode ? '#f8fafc' : '#374151';
  const iconInactiveColor = darkMode ? '#9ca3af' : '#6b7280';
  const labelInactiveColor = darkMode ? '#e5e7eb' : '#374151';
  const toggleIconColor = darkMode ? '#e5e7eb' : '#374151';
  const sectionHeaderColor = darkMode ? '#6b7280' : '#9ca3af';

  // HaliRisk v2 logo (bar chart icon)
  const HaliRiskLogoIcon = ({ size = 20, opacity = 1 }) => (
    <img
      src={darkMode ? '/assets/halirisk-logo-icon-dark.svg' : '/assets/halirisk-logo-icon.svg'}
      alt="HaliRisk v2"
      width={size}
      height={size}
      style={{ opacity, flexShrink: 0, objectFit: 'contain' }}
      aria-hidden
    />
  );

  // EVA logo SVG (red square + white E)
  const EvaLogoIcon = ({ size = 20, opacity = 1 }) => (
    <svg viewBox="0 0 64 64" width={size} height={size} style={{ opacity, flexShrink: 0 }} aria-hidden>
      <defs>
        <linearGradient id="eva-sidebar-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DC2626" />
          <stop offset="100%" stopColor="#B91C1C" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="60" height="60" rx="14" fill="url(#eva-sidebar-grad)" />
      <path d="M14 18h32v3H14v11h26v3H14v11h32v3H14V18z" fill="white" />
    </svg>
  );

  // Bi-color label rendering — logo-style split: base + colored last word
  const renderBiColorLabel = (label, itemColor, isActive, isHovered) => {
    const baseColor = isActive || isHovered
      ? (darkMode ? '#f8fafc' : '#1e293b')
      : labelInactiveColor;

    if (label === 'EVA') {
      return (
        <>
          <span style={{ color: '#DC2626', fontWeight: 700 }}>E</span>
          <span style={{ color: baseColor, fontWeight: 700 }}>VA</span>
        </>
      );
    }
    if (label.startsWith('Hali')) {
      const suffix = label.substring(4);
      return (
        <>
          <span style={{ color: baseColor }}>Hali</span>
          <span style={{ color: itemColor, fontWeight: 700 }}>{suffix}</span>
        </>
      );
    }
    if (label === 'LC DocCheck') {
      return (
        <>
          <span style={{ color: baseColor }}>LC Doc</span>
          <span style={{ color: itemColor, fontWeight: 700 }}>Check</span>
        </>
      );
    }
    const words = label.split(' ');
    if (words.length >= 2) {
      const lastWord = words[words.length - 1];
      const firstPart = words.slice(0, -1).join(' ');
      return (
        <>
          <span style={{ color: baseColor }}>{firstPart} </span>
          <span style={{ color: itemColor, fontWeight: 700 }}>{lastWord}</span>
        </>
      );
    }
    return <span style={{ color: itemColor, fontWeight: 700 }}>{label}</span>;
  };

  // Collect all item paths for smart active matching
  const allItemPaths = useMemo(() => {
    const paths = [];
    if (!menuItems) return paths;
    menuItems.forEach((entry) => {
      if (entry.type === 'section' && entry.children) {
        entry.children.forEach((child) => { if (child.path) paths.push(child.path); });
      } else if (entry.path) {
        paths.push(entry.path);
      }
    });
    return paths;
  }, [menuItems]);

  // Smart active check: an item is active only if it's the longest matching path
  const isItemActive = (itemPath) => {
    if (!activePath || !itemPath) return false;
    if (!activePath.startsWith(itemPath)) return false;
    // Check that no OTHER longer path also matches (prevents parent highlighting when child matches)
    const longerMatch = allItemPaths.find(
      (p) => p !== itemPath && p.length > itemPath.length && activePath.startsWith(p)
    );
    return !longerMatch;
  };

  // Render a single menu item button
  const renderItem = (item, globalIndex) => {
    const isActive = isItemActive(item.path);
    const isHovered = hoveredIndex === globalIndex;
    const activeColor = primaryColor || item.color;
    const itemColor = item.color || activeColor;

    return (
      <button
        key={item.path}
        type="button"
        className={composeClasses('sidebar-item', {
          active: isActive,
          collapsed: effectiveCollapsed
        })}
        style={{
          ...(isActive ? {
            backgroundColor: darkMode ? `${itemColor}15` : `${itemColor}10`,
            borderLeft: `3px solid ${itemColor}`
          } : isHovered ? {
            backgroundColor: darkMode ? `${itemColor}08` : `${itemColor}06`,
            borderLeft: '3px solid transparent'
          } : {
            borderLeft: '3px solid transparent'
          })
        }}
        onClick={() => handleItemClick(item.path, item.external)}
        onMouseEnter={() => setHoveredIndex(globalIndex)}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {item.label === 'EVA' ? (
          <EvaLogoIcon size={20} opacity={isActive ? 1 : isHovered ? 0.9 : 0.6} />
        ) : item.label === 'HaliRisk v2' ? (
          <HaliRiskLogoIcon size={20} opacity={isActive ? 1 : isHovered ? 0.9 : 0.6} />
        ) : (
          <item.icon
            size={20}
            color={isActive || isHovered ? itemColor : itemColor}
            style={{ opacity: isActive ? 1 : isHovered ? 0.9 : 0.6 }}
          />
        )}
        {!effectiveCollapsed && (
          <span className="sidebar-label">
            {renderBiColorLabel(item.label, itemColor, isActive, isHovered)}
          </span>
        )}
        {item.badge && item.badge > 0 && !effectiveCollapsed && (
          <span className="sidebar-badge">{item.badge}</span>
        )}
        {effectiveCollapsed && isHovered && (
          <span className="sidebar-tooltip">{renderBiColorLabel(item.label, itemColor, true, true)}</span>
        )}
      </button>
    );
  };

  // Check if menuItems use the new grouped format (has type: 'section')
  const isGrouped = menuItems.some((item) => item.type === 'section');

  // Build a flat index counter for hover state across sections
  let globalIndex = 0;

  return (
    <aside
      className={composeClasses(
        'collapsible-sidebar',
        className,
        { collapsed: effectiveCollapsed, expanded: !effectiveCollapsed, 'light-mode': !darkMode, 'auto-collapse': effectiveAutoCollapse }
      )}
      style={{ ...style }}
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
    >
      <div className="sidebar-header">
        <div
          className="sidebar-brand"
          onClick={onLogoClick || undefined}
          style={onLogoClick ? { cursor: 'pointer' } : undefined}
          title={onLogoClick ? 'Go to Dashboard' : undefined}
        >
          <SidebarBranding
            logoSrc={logoSrc}
            companyName={whiteLabelCompanyName}
            tagline={subtitle}
            platformName={platformName || brandName}
            viewBadge={viewBadge}
            primaryColor={primaryColor || '#DC2626'}
            darkMode={darkMode}
            collapsed={effectiveCollapsed}
          />
        </div>
        {!effectiveAutoCollapse && (
          <button
            className="sidebar-toggle"
            type="button"
            onClick={handleToggle}
            style={{ borderTopColor: primaryColor ? `${primaryColor}30` : undefined }}
          >
            {effectiveCollapsed
              ? <ChevronRight size={20} style={{ color: primaryColor || toggleIconColor }} />
              : <ChevronLeft size={20} style={{ color: primaryColor || toggleIconColor }} />
            }
          </button>
        )}
      </div>

      <nav className="sidebar-items">
        {isGrouped ? (
          // === GROUPED MODE: sections with collapsible children ===
          menuItems.map((entry) => {
            // Top-level standalone item (no section)
            if (entry.type !== 'section') {
              const idx = globalIndex++;
              return renderItem(entry, idx);
            }

            // Section group
            const section = entry;
            const isExpanded = expandedSections[section.label] !== false; // default open
            const sectionColor = section.color || sectionHeaderColor;
            const hasActiveChild = section.children?.some((child) => isItemActive(child.path));

            return (
              <div key={section.label} className="sidebar-section">
                {/* Section header — clickable to expand/collapse */}
                {!effectiveCollapsed ? (
                  <button
                    type="button"
                    className="sidebar-section-header"
                    onClick={() => toggleSection(section.label)}
                    style={{ color: hasActiveChild ? sectionColor : sectionHeaderColor }}
                  >
                    <span className="sidebar-section-title">
                      {section.label}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`sidebar-section-chevron ${isExpanded ? 'expanded' : ''}`}
                      style={{ color: hasActiveChild ? sectionColor : sectionHeaderColor }}
                    />
                  </button>
                ) : (
                  <div className="sidebar-section-divider" />
                )}

                {/* Section children */}
                {(effectiveCollapsed || isExpanded) && section.children?.map((child) => {
                  const idx = globalIndex++;
                  return renderItem(child, idx);
                })}
              </div>
            );
          })
        ) : (
          // === FLAT MODE: backwards compatible, no sections ===
          menuItems.map((item, index) => renderItem(item, index))
        )}
      </nav>

      {/* Logout button pinned at bottom */}
      {onLogout && (
        <div className="sidebar-footer">
          <button
            type="button"
            aria-label="Logout"
            className={composeClasses('sidebar-logout', { collapsed: effectiveCollapsed })}
            onClick={onLogout}
          >
            <LogOut size={20} style={{ color: '#ef4444', opacity: 0.8 }} />
            {!effectiveCollapsed && (
              <span className="sidebar-logout-label">Logout</span>
            )}
            {effectiveCollapsed && (
              <span className="sidebar-tooltip">Logout</span>
            )}
          </button>
        </div>
      )}
    </aside>
  );
};

CollapsibleSidebar.propTypes = {
  logoSrc: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  viewBadge: PropTypes.string,
  whiteLabelCompanyName: PropTypes.string,
  menuItems: PropTypes.array.isRequired,
  activePath: PropTypes.string,
  onNavigate: PropTypes.func.isRequired,
  onLogoClick: PropTypes.func,
  onToggle: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  primaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  darkMode: PropTypes.bool,
  autoCollapse: PropTypes.bool,
  onLogout: PropTypes.func
};

export default CollapsibleSidebar;
